// src/components/Admin/DeviceRegistration.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getLocationName } from '../../utils/locationUtils';
import './DeviceRegistration.css';

const DeviceRegistration = () => {
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  // Nuovo dispositivo: è l'unico punto in cui nascono ID e nomi dei kiosk
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceLocation, setNewDeviceLocation] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Carica dispositivi e locations all'avvio
  useEffect(() => {
    loadDevices();
    loadLocations();
  }, []);

  // Carica dispositivi kiosk
  const loadDevices = async () => {
    try {
      setLoading(true);
      const devicesRef = collection(db, 'scanDevices');  // ✅ Collezione corretta
      const snapshot = await getDocs(devicesRef);
      
      console.log('📦 Documenti trovati in scanDevices:', snapshot.size);
      
      // Carica dispositivi con struttura scanDevices
      const devicesList = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        
        // ✅ Struttura scanDevices:
        // - deviceInfo.deviceType, deviceInfo.kioskMode, ecc.
        // - deviceName a livello root
        // - assignedLocation a livello root
        
        return {
          id: docSnap.id,  // Nome documento (es. "CLS Pistavecchia")
          deviceName: data.deviceName || docSnap.id,
          active: data.active !== undefined ? data.active : true,
          
          // Da deviceInfo map
          deviceType: data.deviceInfo?.deviceType || 'unknown',
          kioskMode: data.deviceInfo?.kioskMode || false,
          
          // Timestamps (Firestore Timestamp)
          registeredAt: data.deviceInfo?.registeredAt || data.createdAt,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          lastActivity: data.lastActivity,
          
          // Build dell'app in uso sul tablet (timestamp, scritto dal kiosk all'avvio)
          appBuild: data.appBuild || null,

          // Posizione
          assignedLocation: data.assignedLocation || null,
          locationName: null  // Sarà caricato dopo
        };
      });
      
      // Ordina per ultima attività (più recenti prima)
      devicesList.sort((a, b) => {
        const dateA = a.lastActivity?.toDate?.() || a.updatedAt?.toDate?.() || new Date(a.registeredAt || 0);
        const dateB = b.lastActivity?.toDate?.() || b.updatedAt?.toDate?.() || new Date(b.registeredAt || 0);
        return dateB - dateA;
      });
      
      console.log('✅ Dispositivi base caricati:', devicesList.length);
      
      // Debug: mostra struttura primo dispositivo
      if (devicesList.length > 0) {
        console.log('📋 Esempio dispositivo:', {
          id: devicesList[0].id,
          deviceName: devicesList[0].deviceName,
          active: devicesList[0].active,
          kioskMode: devicesList[0].kioskMode,
          assignedLocation: devicesList[0].assignedLocation,
          registeredAt: devicesList[0].registeredAt
        });
      }
      
      setDevices(devicesList);
      
      // Carica nomi posizioni in background (senza bloccare UI)
      loadLocationNamesAsync(devicesList);
      
    } catch (error) {
      console.error('❌ Errore caricamento dispositivi:', error);
      showNotification('Errore caricamento dispositivi: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Carica nomi posizioni in background
  const loadLocationNamesAsync = async (devicesList) => {
    try {
      const updatedDevices = await Promise.all(
        devicesList.map(async (device) => {
          if (device.assignedLocation) {
            try {
              const locationName = await getLocationName(device.assignedLocation);
              return { ...device, locationName };
            } catch (error) {
              console.error(`Errore nome per ${device.assignedLocation}:`, error);
              return { ...device, locationName: device.assignedLocation };
            }
          }
          return device;
        })
      );
      
      console.log('✅ Nomi posizioni caricati');
      setDevices(updatedDevices);
    } catch (error) {
      console.error('⚠️ Errore caricamento nomi (non critico):', error);
    }
  };

  // Carica cantieri e officina
  const loadLocations = async () => {
    try {
      // Carica cantieri attivi
      const cantieriRef = collection(db, 'cantieri');
      const cantieriQuery = query(cantieriRef, where('stato', '==', 'Attivo'));
      const cantieriSnap = await getDocs(cantieriQuery);
      
      const locationsList = cantieriSnap.docs.map(doc => ({
        id: doc.id,
        nome: doc.data().nomeCommessa || doc.data().nome,
        tipo: 'cantiere'
      }));
      
      // Aggiungi Officina Pistavecchia
      locationsList.unshift({
        id: 'officina-pistavecchia',
        nome: 'Officina Pistavecchia',
        tipo: 'officina'
      });
      
      setLocations(locationsList);
    } catch (error) {
      console.error('Errore caricamento locations:', error);
      showNotification('Errore caricamento posizioni', 'error');
    }
  };

  // Toggle stato dispositivo (attivo/inattivo)
  const toggleDeviceStatus = async (deviceId, currentStatus) => {
    try {
      const deviceRef = doc(db, 'scanDevices', deviceId);  // ✅ Collezione corretta
      await updateDoc(deviceRef, {
        active: !currentStatus,
        updatedAt: new Date()  // ✅ Timestamp Firestore
      });
      
      showNotification(
        `Dispositivo ${!currentStatus ? 'attivato' : 'disattivato'} con successo`,
        'success'
      );
      
      loadDevices();
    } catch (error) {
      console.error('Errore toggle stato:', error);
      showNotification('Errore modifica stato dispositivo', 'error');
    }
  };

  // Elimina dispositivo
  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo dispositivo?')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'scanDevices', deviceId));  // ✅ Collezione corretta
      showNotification('Dispositivo eliminato con successo', 'success');
      loadDevices();
    } catch (error) {
      console.error('Errore eliminazione dispositivo:', error);
      showNotification('Errore eliminazione dispositivo', 'error');
    }
  };

  // Apri modal assegnazione posizione
  const openLocationModal = (device) => {
    setSelectedDevice(device);
    setSelectedLocation(device.assignedLocation || '');
    setShowLocationModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewDeviceId('');
    setNewDeviceName('');
    setNewDeviceLocation('');
  };

  // Crea un dispositivo kiosk. I tablet in configurazione possono solo
  // scegliere tra i dispositivi creati qui: niente più duplicati nati da un
  // nome digitato in modo leggermente diverso.
  const createDevice = async () => {
    const id = newDeviceId.trim();
    const name = newDeviceName.trim();

    if (!id || !name) {
      showNotification('ID e nome del dispositivo sono obbligatori', 'warning');
      return;
    }
    // Firestore non accetta "/" negli ID documento
    if (id.includes('/') || id === '.' || id === '..') {
      showNotification('L\'ID non può contenere "/"', 'warning');
      return;
    }

    try {
      setIsSaving(true);
      const deviceRef = doc(db, 'scanDevices', id);
      const existing = await getDoc(deviceRef);
      if (existing.exists()) {
        showNotification(`Esiste già un dispositivo con ID "${id}"`, 'warning');
        return;
      }

      await setDoc(deviceRef, {
        deviceName: name,
        active: true,
        assignedLocation: newDeviceLocation || null,
        createdByAdmin: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      showNotification(`Dispositivo "${name}" creato`, 'success');
      closeCreateModal();
      loadDevices();
    } catch (error) {
      console.error('Errore creazione dispositivo:', error);
      showNotification('Errore creazione dispositivo: ' + error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Rinomina: l'ID resta invariato (è ciò che lega tablet, posizione e
  // timbrature), cambia solo il nome mostrato. I kiosk lo riprendono al riavvio.
  const renameDevice = async (device) => {
    const newName = window.prompt('Nuovo nome del dispositivo:', device.deviceName || device.id);
    if (newName === null) return;
    const name = newName.trim();
    if (!name || name === device.deviceName) return;

    try {
      await updateDoc(doc(db, 'scanDevices', device.id), {
        deviceName: name,
        updatedAt: serverTimestamp()
      });
      showNotification('Nome aggiornato', 'success');
      loadDevices();
    } catch (error) {
      console.error('Errore rinomina dispositivo:', error);
      showNotification('Errore rinomina dispositivo', 'error');
    }
  };

  // Chiudi modal
  const closeLocationModal = () => {
    setShowLocationModal(false);
    setSelectedDevice(null);
    setSelectedLocation('');
  };

  // Salva posizione assegnata
  const saveLocation = async () => {
    if (!selectedLocation) {
      showNotification('Seleziona una posizione', 'warning');
      return;
    }
    
    try {
      const deviceRef = doc(db, 'scanDevices', selectedDevice.id);  // ✅ Collezione corretta
      await updateDoc(deviceRef, {
        assignedLocation: selectedLocation,
        updatedAt: new Date()  // ✅ Timestamp Firestore
      });
      
      showNotification('Posizione assegnata con successo', 'success');
      closeLocationModal();
      loadDevices();
    } catch (error) {
      console.error('Errore salvataggio posizione:', error);
      showNotification('Errore assegnazione posizione', 'error');
    }
  };

  // Rimuovi posizione assegnata
  const removeLocation = async (deviceId) => {
    if (!window.confirm('Rimuovere la posizione assegnata a questo dispositivo?')) {
      return;
    }
    
    try {
      const deviceRef = doc(db, 'scanDevices', deviceId);  // ✅ Collezione corretta
      await updateDoc(deviceRef, {
        assignedLocation: null,
        updatedAt: new Date()  // ✅ Timestamp Firestore
      });
      
      showNotification('Posizione rimossa con successo', 'success');
      loadDevices();
    } catch (error) {
      console.error('Errore rimozione posizione:', error);
      showNotification('Errore rimozione posizione', 'error');
    }
  };

  // Mostra notifica
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Formatta data
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    let date;
    
    // ✅ Gestisci Timestamp Firestore
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    }
    // Gestisci string ISO
    else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    }
    // Gestisci Date object
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    else {
      return 'N/A';
    }
    
    // Verifica che sia una data valida
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="device-registration-container">
      {/* Header */}
      <div className="device-header">
        <h2>🖥️ Gestione Dispositivi Kiosk</h2>
        <p>Gestisci i dispositivi di timbratura e assegna le posizioni</p>
        <button
          className="btn-save-location"
          onClick={() => setShowCreateModal(true)}
          style={{ marginTop: '10px' }}
        >
          ➕ Nuovo dispositivo
        </button>
      </div>

      {/* Notifica */}
      {notification.show && (
        <div className={`device-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="device-loading">
          <div className="spinner"></div>
          <p>Caricamento dispositivi...</p>
        </div>
      )}

      {/* Lista dispositivi */}
      {!loading && (
        <>
          {devices.length === 0 ? (
            <div className="no-devices">
              <p>📱 Nessun dispositivo registrato</p>
              <p className="hint">
                Crea un dispositivo con "Nuovo dispositivo", poi selezionalo sul tablet in fase di configurazione kiosk
              </p>
            </div>
          ) : (
            <div className="devices-table-container">
              <table className="devices-table">
                <thead>
                  <tr>
                    <th>Dispositivo</th>
                    <th>ID</th>
                    <th>Posizione Assegnata</th>
                    <th>Stato</th>
                    <th>Registrato</th>
                    <th>Versione app</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(device => (
                    <tr key={device.id} className={!device.active ? 'inactive' : ''}>
                      {/* Nome dispositivo */}
                      <td className="device-name">
                        <div className="device-info">
                          <span className="name">{device.deviceName || device.id}</span>
                          {device.kioskMode && (
                            <span className="badge kiosk-badge">KIOSK</span>
                          )}
                        </div>
                      </td>

                      {/* ID */}
                      <td className="device-id">
                        <code>{device.id}</code>
                      </td>

                      {/* Posizione */}
                      <td className="device-location">
                        {device.assignedLocation ? (
                          <div className="location-assigned">
                            <span className="location-icon">
                              {device.locationName?.includes('Officina') ? '🔧' : '🏗️'}
                            </span>
                            <span className="location-name">{device.locationName}</span>
                            <button
                              className="btn-remove-location"
                              onClick={() => removeLocation(device.id)}
                              title="Rimuovi posizione"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span className="no-location">Non assegnata</span>
                        )}
                      </td>

                      {/* Stato */}
                      <td className="device-status">
                        <span className={`status-badge ${device.active ? 'active' : 'inactive'}`}>
                          {device.active ? '✓ Attivo' : '✗ Inattivo'}
                        </span>
                      </td>

                      {/* Data registrazione */}
                      <td className="device-date">
                        {formatDate(device.registeredAt)}
                      </td>

                      {/* Build in uso sul tablet: serve a verificare che abbia preso l'ultimo deploy */}
                      <td className="device-date">
                        {device.appBuild ? formatDate(new Date(device.appBuild)) : 'N/A'}
                      </td>

                      {/* Azioni */}
                      <td className="device-actions">
                        <button
                          className="btn-assign-location"
                          onClick={() => renameDevice(device)}
                          title="Rinomina dispositivo"
                        >
                          ✏️ Nome
                        </button>

                        <button
                          className="btn-assign-location"
                          onClick={() => openLocationModal(device)}
                          title="Assegna posizione"
                        >
                          📍 Posizione
                        </button>
                        
                        <button
                          className={`btn-toggle ${device.active ? 'btn-deactivate' : 'btn-activate'}`}
                          onClick={() => toggleDeviceStatus(device.id, device.active)}
                          title={device.active ? 'Disattiva' : 'Attiva'}
                        >
                          {device.active ? '⏸️' : '▶️'}
                        </button>
                        
                        <button
                          className="btn-delete"
                          onClick={() => deleteDevice(device.id)}
                          title="Elimina dispositivo"
                        >
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Info panel */}
          <div className="device-info-panel">
            <h4>ℹ️ Informazioni Dispositivi Kiosk</h4>
            <ul>
              <li>
                <strong>Registrazione:</strong> Crea il dispositivo qui con "Nuovo dispositivo".
                Sul tablet, in configurazione kiosk, si sceglie il dispositivo dall'elenco e si
                inserisce la chiave KIOSK2025: ID e nome non si digitano più sul tablet
              </li>
              <li>
                <strong>Posizione:</strong> Assegna una posizione fissa (cantiere o officina) 
                ad ogni dispositivo kiosk per determinare dove vengono timbrate le ore
              </li>
              <li>
                <strong>Stato:</strong> Disattiva un dispositivo per impedire temporaneamente 
                le timbrature (es. manutenzione)
              </li>
              <li>
                <strong>Uscita Emergency:</strong> Premi ESC o 5 tap nell'angolo in alto a sinistra 
                per uscire dalla modalità Kiosk
              </li>
            </ul>
          </div>
        </>
      )}

      {/* Modal Nuovo Dispositivo */}
      {showCreateModal && (
        <div className="location-modal-overlay" onClick={closeCreateModal}>
          <div className="location-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Nuovo Dispositivo Kiosk</h3>
              <button className="btn-close-modal" onClick={closeCreateModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="location-select-group">
                <label htmlFor="new-device-id">ID dispositivo (non modificabile dopo la creazione):</label>
                <input
                  id="new-device-id"
                  type="text"
                  className="location-select"
                  value={newDeviceId}
                  onChange={(e) => setNewDeviceId(e.target.value)}
                  placeholder="es. tablet-officina-01"
                  autoComplete="off"
                />
              </div>

              <div className="location-select-group">
                <label htmlFor="new-device-name">Nome dispositivo:</label>
                <input
                  id="new-device-name"
                  type="text"
                  className="location-select"
                  value={newDeviceName}
                  onChange={(e) => setNewDeviceName(e.target.value)}
                  placeholder="es. Tablet Officina Pistavecchia"
                  autoComplete="off"
                />
              </div>

              <div className="location-select-group">
                <label htmlFor="new-device-location">Posizione (facoltativa, assegnabile anche dopo):</label>
                <select
                  id="new-device-location"
                  value={newDeviceLocation}
                  onChange={(e) => setNewDeviceLocation(e.target.value)}
                  className="location-select"
                >
                  <option value="">-- Nessuna posizione --</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.tipo === 'officina' ? '🔧' : '🏗️'} {location.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeCreateModal}>
                Annulla
              </button>
              <button
                className="btn-save-location"
                onClick={createDevice}
                disabled={isSaving || !newDeviceId.trim() || !newDeviceName.trim()}
              >
                {isSaving ? 'Salvataggio...' : '💾 Crea dispositivo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Assegnazione Posizione */}
      {showLocationModal && (
        <div className="location-modal-overlay" onClick={closeLocationModal}>
          <div className="location-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📍 Assegna Posizione Kiosk</h3>
              <button className="btn-close-modal" onClick={closeLocationModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="device-info-modal">
                <p><strong>Dispositivo:</strong> {selectedDevice?.deviceName || selectedDevice?.id}</p>
                <p><strong>ID:</strong> <code>{selectedDevice?.id}</code></p>
              </div>

              <div className="location-select-group">
                <label htmlFor="location-select">Seleziona Posizione:</label>
                <select
                  id="location-select"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="location-select"
                >
                  <option value="">-- Seleziona posizione --</option>
                  {locations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.tipo === 'officina' ? '🔧' : '🏗️'} {location.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-info">
                <p className="info-text">
                  ℹ️ Quando un dipendente timbra da questo dispositivo, 
                  le ore verranno automaticamente assegnate alla posizione selezionata.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeLocationModal}>
                Annulla
              </button>
              <button 
                className="btn-save-location" 
                onClick={saveLocation}
                disabled={!selectedLocation}
              >
                💾 Salva Posizione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceRegistration;